/**
 * Extract a user-facing error message from an API error response.
 *
 * The backend render() helper wraps responses as:
 *   { data: { message/error: "..." }, statusCode: {...} }
 *
 * Some endpoints also return flat shapes:
 *   { error: "..." }      (controllers, newer endpoints)
 *   { message: "..." }    (middleware, older endpoints)
 *   { details: "..." }    (rare, extra context)
 *
 * This helper checks all possible paths, falling back to a generic message.
 *
 * @param {Error} error - Axios error (or any error with .response)
 * @param {string} [fallback='Something went wrong. Please try again.'] - Default message
 * @returns {string} User-facing error message
 */
export function getApiErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
    if (!error) return fallback;

    const data = error.response?.data;
    if (!data) return error.message || fallback;
    const wrappedData = data.data;

    // Check all possible backend response shapes:
    // 1. render() wrapper: { data: { message/error: "..." } }
    //    or legacy wrapper: { data: "..." }
    // 2. Direct response: { message/error: "..." }
    // 3. Nested details
    return (typeof wrappedData === 'string' && wrappedData)
        || wrappedData?.message
        || wrappedData?.error
        || data.error
        || data.message
        || data.details
        || fallback;
}

/**
 * Check if an API error is a permission/authorization error (403).
 *
 * @param {Error} error - Axios error
 * @returns {boolean}
 */
export function isPermissionError(error) {
    return error?.response?.status === 403;
}

/**
 * Check if an API error is a rate-limit error (429).
 *
 * @param {Error} error - Axios error
 * @returns {boolean}
 */
export function isRateLimitError(error) {
    return error?.response?.status === 429;
}

/**
 * Get a user-friendly message for rate-limit errors on authentication endpoints.
 *
 * @param {Error} error - Axios error
 * @returns {string|null} A rate-limit message, or null if the error is not a 429.
 */
export function getRateLimitMessage(error) {
    if (!isRateLimitError(error)) return null;
    return (
        error.response?.data?.message ||
        'Too many login attempts from your network — please try again in 15 minutes.'
    );
}
